import { useEffect, useState, useCallback } from 'react';
import { useWebSocket } from '../providers/WebSocketProvider';
import { notify } from '@/lib/notify';
import { apiClient } from '../providers/api-client';
import { formatAdminNotification } from '../shared/admin-notification-format';

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  sentAt: string;
  linkUrl?: string;
  readAt?: string | null;
  actorAdminId?: number | null;
  actorUserId?: number | null;
  actorName?: string | null;
}

export const useAdminNotifications = () => {
  const { lastMessage, isConnected } = useWebSocket();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch initial notifications from REST API
  // apiClient.get<T>() already unwraps json.data, so it returns T directly
  const fetchNotifications = useCallback(async () => {
    try {
      const items = await apiClient.get<Notification[]>(
        '/admin/notifications?limit=50'
      );
      setNotifications(items ?? []);

      const countData = await apiClient.get<{ count: number }>(
        '/admin/notifications/unread-count'
      );
      setUnreadCount(countData.count ?? 0);
    } catch (err) {
      console.error('Failed to fetch admin notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  // Handle real-time WebSocket messages
  useEffect(() => {
    if (!lastMessage) { return; }

    const msg = lastMessage as { type: string; data?: Notification; count?: number };

    if (msg.type === 'notification' && msg.data) {
      const newNotification = msg.data;
      setNotifications(prev => {
        // Avoid duplicates
        if (prev.some(n => n.id === newNotification.id)) { return prev; }
        return [newNotification, ...prev];
      });

      // Show toast — типизированный уровень по NotificationType
      const visual = formatAdminNotification(newNotification.type);
      notify[visual.toastLevel](newNotification.title, {
        description: newNotification.message,
      });
    }

    if (msg.type === 'unread_count' && typeof msg.count === 'number') {
      setUnreadCount(msg.count);
    }
  }, [lastMessage]);

  const markAsRead = useCallback(async (id: number) => {
    // Optimistic update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await fetch(
        `${getApiUrl()}/admin/notifications/${String(id)}/read`,
        { method: 'PATCH', credentials: 'include' }
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // Revert on error
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: false } : n));
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const prevNotifications = notifications;
    const prevUnreadCount = unreadCount;

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);

    try {
      await fetch(
        `${getApiUrl()}/admin/notifications/read-all`,
        { method: 'PATCH', credentials: 'include' }
      );
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      // Revert on error
      setNotifications(prevNotifications);
      setUnreadCount(prevUnreadCount);
    }
  }, [notifications, unreadCount]);

  return {
    notifications,
    unreadCount,
    isConnected,
    isLoading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
};

function getApiUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!import.meta.env.DEV && envUrl && envUrl.length > 0) {
    const trimmed = envUrl.replace(/\/+$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
  }
  if (typeof window !== 'undefined') {
    const origin = window.location.origin.replace(/\/+$/, '');
    return `${origin}/api`;
  }
  return 'https://localhost:3000/api';
}
